import * as Sentry from "@sentry/react";
import { ChatJson } from "./types";

export const MONITORING_DOCUMENT_TYPE_ID = "a1f0e6c2-4b7d-4e51-9a3a-6b9f7e2c5d10";

// Six labeled sections the grid renders under, following the reference VCA overnight-monitoring
// treatment sheet: two fixed-row sections (vital_signs, nutrition) and four free-form repeating
// sections for entries the tech calls out during a check-in. API-Server maintains an independent
// copy of this exact type/value set (no shared package) — keep them in sync.
export type MonitoringSectionKey = "vital_signs" | "nutrition" | "medications" | "treatments" | "labs" | "procedures";

export const MONITORING_SECTION_LABELS: Record<MonitoringSectionKey, string> = {
    vital_signs: "Vital Signs",
    nutrition: "Nutrition",
    medications: "Medications",
    treatments: "Treatments",
    labs: "Labs",
    procedures: "Procedures",
};

export const MONITORING_REPEATING_SECTION_KEYS: MonitoringSectionKey[] = [
    "medications",
    "treatments",
    "labs",
    "procedures",
];

export interface MonitoringRowDef {
    id: string;
    label: string;
    valueType: "numeric" | "text";
    unit?: string;
    repeating?: boolean;
    section: MonitoringSectionKey;
}

export interface MonitoringGridColumn {
    id: string;
    timestamp: string; // ISO 8601
}

export interface MonitoringGridPayload {
    rowDefs: MonitoringRowDef[];
    columns: MonitoringGridColumn[];
    cells: Record<string, Record<string, string>>; // cells[rowId][columnId]
}

// A check-in that named an already-filled row/column and was dropped by API-Server's append-only
// merge guard (see app/domain/monitoringMerge.ts) instead of overwriting it. Mirrors that repo's
// DiscardedMonitoringCheck shape exactly, since it's serialized as-is over the wire.
export interface DiscardedMonitoringCheck {
    rowId: string;
    rowLabel: string;
    columnId: string;
    timestamp: string;
    discardedValue: string;
    existingValue: string;
}

// Changing this list (renaming/splitting/adding a row) is NOT a breaking schema change for
// already-persisted documents: a document's `rowDefs` is snapshotted into its own `chat_json` once,
// at creation (API-Server's `buildEmptyMonitoringGrid()`), and this frontend only ever renders
// whatever `rowDefs` a document already has — nothing here re-derives a document's rowDefs from
// this constant later, and `MONITORING_FIXED_ROW_DEFS` itself has no production consumer in this
// repo at all (test-only mirror of the API-Server copy). So an existing document's historical
// cells stay exactly as recorded, under their original row, forever.
//
// What a rename/split DOES require: API-Server's `monitoringMerge.ts` (`resolveRowId`) must
// self-heal when a NEW extraction targets an OLD document whose rowDefs don't have the new row id
// yet (extraction is always built from the CURRENT version of this list, regardless of which
// document a check-in will end up merging into — see that file's comment for why). Any code that
// reads a row's cells across a document's whole history (export mappers, reports, etc.) must
// branch on which cells are actually populated per column, never on rowDefs shape alone, since a
// single document can legitimately carry both an old and a self-healed new row for the same vital.
export const MONITORING_FIXED_ROW_DEFS: MonitoringRowDef[] = [
    { id: "temp", label: "TEMP", valueType: "numeric", unit: "°F", section: "vital_signs" },
    { id: "resp_rate", label: "Resp Rate", valueType: "numeric", unit: "/min", section: "vital_signs" },
    { id: "heart_rate", label: "Heart Rate", valueType: "numeric", unit: "bpm", section: "vital_signs" },
    { id: "outside_litterbox", label: "Outside/Litterbox", valueType: "text", section: "vital_signs" },
    { id: "food_amount", label: "Food Amount", valueType: "text", section: "nutrition" },
    { id: "food_type", label: "Food Type", valueType: "text", section: "nutrition" },
    { id: "water_amount", label: "Water Amount", valueType: "text", section: "nutrition" },
    { id: "body_weight", label: "Body Weight", valueType: "numeric", unit: "lb", section: "nutrition" },
    { id: "fluid_1_rate", label: "Fluid #1 Rate", valueType: "text", section: "nutrition" },
    { id: "fluid_2_rate", label: "Fluid #2 Rate", valueType: "text", section: "nutrition" },
    { id: "fluid_3_rate", label: "Fluid #3 Rate", valueType: "text", section: "nutrition" },
    { id: "check_rates_iv_catheters", label: "Check Rates & IV Catheter(s)", valueType: "text", section: "nutrition" },
];

// Fixed display order regardless of the order rows happen to appear in a payload: the two
// fixed-row sections first, then the four free-form repeating sections in a stable order.
const SECTION_DISPLAY_ORDER: MonitoringSectionKey[] = [
    "vital_signs",
    "nutrition",
    ...MONITORING_REPEATING_SECTION_KEYS,
];

// Copy shown for a repeating section (medications/treatments/labs/procedures) with nothing
// recorded yet — distinguishes "checked, nothing to report" from a row simply not existing.
const NO_ENTRIES_LABEL = "No entries recorded";

// Catch-all bucket key for a rowDef whose `section` isn't one of the six known
// MonitoringSectionKey values — e.g. a payload from a rolling API-Server deploy that's added a
// section this frontend build doesn't know about yet, or a malformed/typo'd value. Distinct from
// MonitoringSectionKey by construction (not a member of that union) so callers can't confuse it
// with a real section.
const OTHER_SECTION_KEY = "other";

export interface MonitoringSectionGroup {
    key: MonitoringSectionKey | typeof OTHER_SECTION_KEY;
    label: string;
    rows: MonitoringRowDef[];
    // True when this section has no real rows and `rows` contains a single synthetic
    // placeholder instead — never a row with an id present in the grid's `cells`.
    isPlaceholder: boolean;
}

/**
 * Groups a grid's rowDefs into one entry per Monitoring section, in the fixed display order,
 * always emitting all six sections. The two fixed-row sections (vital_signs, nutrition) always
 * have real rows in practice (seeded by MONITORING_FIXED_ROW_DEFS), but a repeating section
 * (medications/treatments/labs/procedures) with nothing recorded yet gets a single synthetic
 * placeholder row instead of being omitted — so the rendered table/print output always shows
 * all four repeating sections, not just the ones something happened to be charted under.
 *
 * A rowDef whose `section` isn't one of the six known keys (API-Server maintains an independent
 * copy of MonitoringSectionKey with no shared package/schema — see the type above) is never
 * dropped: it's surfaced under a trailing "Other" group and reported to Sentry, since this is
 * clinical data and a silently vanished row is a patient-record display defect, not a cosmetic one.
 */
export function groupMonitoringRows(rowDefs: MonitoringRowDef[]): MonitoringSectionGroup[] {
    const rowsBySection = new Map<string, MonitoringRowDef[]>();
    for (const row of rowDefs) {
        const rows = rowsBySection.get(row.section) ?? [];
        rows.push(row);
        rowsBySection.set(row.section, rows);
    }

    const knownSections = new Set<string>(SECTION_DISPLAY_ORDER);
    const unrecognizedRows = rowDefs.filter((row) => !knownSections.has(row.section));
    if (unrecognizedRows.length > 0) {
        Sentry.captureMessage("Monitoring grid rowDefs contain unrecognized section values", {
            level: "warning",
            extra: {
                unrecognizedSections: Array.from(new Set(unrecognizedRows.map((row) => row.section))),
                rowCount: unrecognizedRows.length,
            },
        });
    }

    const groups: MonitoringSectionGroup[] = SECTION_DISPLAY_ORDER.map((sectionKey) => {
        const rows = rowsBySection.get(sectionKey);
        if (rows && rows.length > 0) {
            return { key: sectionKey, label: MONITORING_SECTION_LABELS[sectionKey], rows, isPlaceholder: false };
        }
        const placeholderRow: MonitoringRowDef = {
            id: `${sectionKey}-empty`,
            label: NO_ENTRIES_LABEL,
            valueType: "text",
            section: sectionKey,
            repeating: true,
        };
        return {
            key: sectionKey,
            label: MONITORING_SECTION_LABELS[sectionKey],
            rows: [placeholderRow],
            isPlaceholder: true,
        };
    });

    if (unrecognizedRows.length > 0) {
        groups.push({ key: OTHER_SECTION_KEY, label: "Other", rows: unrecognizedRows, isPlaceholder: false });
    }

    return groups;
}

export function getMonitoringGridFromChatJson(chatJson: ChatJson | null | undefined): MonitoringGridPayload | null {
    const chatText = chatJson?.chat_text;
    if (Array.isArray(chatText)) {
        // Locate the grid section rather than assuming index 0 — API-Server may prepend a
        // non-grid "Patient Information" header section before it (see PersistMonitoringGrid on
        // that side, which already finds the grid section by property for the same reason).
        const gridSection = chatText.find((section) => section?.grid);
        if (gridSection?.grid) {
            return gridSection.grid;
        }
    }
    return null;
}

// Mirrors API-Server's toClinicDateKey (app/domain/monitoringMerge.ts) exactly — same
// Intl.DateTimeFormat technique — so this client-side check agrees with the backend's
// authoritative monitoring_date_key day-bucketing for the same timezone.
function toDateKeyInTimeZone(date: Date, timeZone: string): string {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
        date,
    );
}

/**
 * True when the grid's most recent column falls on an earlier calendar day than `referenceDate`,
 * both evaluated in `timeZone` (the clinic's timezone, not the browser's) — this is a client-side
 * prompt only; the backend's `monitoring_date_key` remains the authoritative record of which day
 * a document covers. Using the clinic's timezone here keeps this hint from disagreeing with that
 * backend day-bucketing for a clinic in a different timezone than the browser, or a traveling
 * staff member.
 */
export function isMonitoringGridStale(grid: MonitoringGridPayload, referenceDate: Date, timeZone: string): boolean {
    if (grid.columns.length === 0) {
        return false;
    }
    const latestColumnMs = Math.max(...grid.columns.map((c) => new Date(c.timestamp).getTime()));
    const latestColumnDate = new Date(latestColumnMs);
    return toDateKeyInTimeZone(latestColumnDate, timeZone) < toDateKeyInTimeZone(referenceDate, timeZone);
}

/**
 * At-rest display value for a grid cell. Medications-section cells collapse only the exact "given"
 * sentinel to a bare "X" — the row label (drug name/dose) already carries the real detail, so a
 * confirmed administration only needs to signal presence, not repeat text. Any other value (e.g.
 * "refused") renders as-is so a declined medication stays visually distinguishable from one that
 * was given. Every other section renders its stored value unchanged. This is the one place both
 * the live grid view and the print HTML source their cell display from, so they can't drift out of
 * sync with each other.
 */
export function formatMonitoringCellValue(sectionKey: MonitoringSectionKey, rawValue: string | undefined): string {
    if (sectionKey === "medications" && rawValue?.trim().toLowerCase() === "given") {
        return "X";
    }
    return rawValue ?? "";
}

/**
 * Strips Monitoring out of a shared document-type selection for surfaces that can't act on it.
 * The recording widget's own picker already excludes Monitoring from its checkbox list, but that
 * list is a view over the SAME `selectedDocuments` Redux state the main sidebar's DocumentSelect
 * populates (where selecting Monitoring is correct — GenerateButton's monitoringSelected split
 * routes it to the retroactive batch endpoint there). If Monitoring ends up in that shared state
 * by any other path, the widget's own "how many are selected" / "is Generate armed" logic must
 * disregard it too — generate-on-stop can never act on it (transcribeAudio filters it out before
 * sending), so counting it toward "something is selected" arms a control that silently produces
 * no Monitoring output.
 */
export function excludeMonitoringId(documentIds: string[]): string[] {
    return documentIds.filter((id) => id !== MONITORING_DOCUMENT_TYPE_ID);
}

/**
 * Monitoring is shared to every account at the document_sharing layer (target_account_id is NULL
 * — see migration 20260820160001_grant_monitoring_document_type_access.sql) regardless of
 * account_settings.monitoring_enabled, which gates the gradual rollout instead. Filtering it back
 * out of a document-type list here — the one place a document-type fetch merges the account's own
 * and shared document types — gives every caller a single enforcement point, instead of each
 * UI/mutation call site needing to independently re-check the flag (and risk one that doesn't).
 */
export function filterMonitoringDocumentTypeByFlag<T extends { id: string }>(
    documentTypes: T[],
    monitoringEnabled: boolean | null | undefined,
): T[] {
    if (monitoringEnabled === true) {
        return documentTypes;
    }
    return documentTypes.filter((doc) => doc.id !== MONITORING_DOCUMENT_TYPE_ID);
}
